import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ShieldCheck, HeartHandshake, MessageCircle, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Página pública e compartilhável: moracomigo.com.br/listadeespera (?origem=instagram
// marca a campanha). Os dados vão para POST /api/waitlist; a resposta é sempre a
// mesma, mesmo para quem já estava inscrita.
const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR",
  "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

const BENEFITS = [
  { icon: ShieldCheck, title: "Perfis verificados", desc: "Validações de documentos e histórico para mais segurança." },
  { icon: HeartHandshake, title: "Matches por afinidade", desc: "Conexões compatíveis com seu estilo de vida." },
  { icon: MessageCircle, title: "Conversa só após o match", desc: "Chat liberado quando o interesse é recíproco." },
];

const EMPTY = { name: "", email: "", whatsapp: "", city: "", state: "", website: "" };

function maskPhone(value) {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function validate(form, accepted) {
  const err = {};
  if (form.name.trim().length < 2) err.name = "Conte como podemos te chamar.";
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) err.email = "Informe um e-mail válido.";
  const digits = form.whatsapp.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 11) err.whatsapp = "Informe DDD + número.";
  if (form.city.trim().length < 2) err.city = "Informe sua cidade.";
  if (!form.state) err.state = "Selecione o estado.";
  if (!accepted) err.privacy = "Para continuar, aceite a Política de Privacidade.";
  return err;
}

function apiError(err) {
  if (err.response?.status === 429) return "Muitas tentativas seguidas. Aguarde alguns minutos e tente de novo.";
  if (!err.response) return "Não foi possível conectar. Verifique sua internet e tente de novo.";
  return formatApiErrorDetail(err.response?.data?.detail).replace(/Value error, /g, "");
}

export default function ListaDeEspera() {
  const { search } = useLocation();
  const source = useMemo(() => {
    const qs = new URLSearchParams(search);
    return qs.get("origem") || qs.get("utm_source") || undefined;
  }, [search]);
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const sending = useRef(false);
  const doneRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = "Lista de espera | MoraComigo";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (status === "done") doneRef.current?.focus();
  }, [status]);

  const set = (k) => (e) => {
    const value = k === "whatsapp" ? maskPhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending.current || status === "done") return; // evita envio duplo (duplo clique/Enter)
    const err = validate(form, accepted);
    setErrors(err);
    setFormError("");
    if (Object.keys(err).length) {
      document.getElementById(`le-${Object.keys(err)[0]}`)?.focus();
      return;
    }
    sending.current = true;
    setStatus("sending");
    try {
      await api.post("/waitlist", {
        name: form.name.trim(), email: form.email.trim(), whatsapp: form.whatsapp, city: form.city.trim(),
        state: form.state, privacy_accepted: accepted, source, website: form.website || undefined,
      });
      setStatus("done");
    } catch (error) {
      setFormError(apiError(error));
      setStatus("idle");
    } finally {
      sending.current = false;
    }
  };

  const firstName = form.name.trim().split(/\s+/)[0];

  return (
    <div data-testid="page-lista-de-espera" className="mc-grad-hero">
      <section className="mc-container py-8 sm:py-12 lg:py-16 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-14 items-start">
        <div className="mc-fade-up lg:pt-6">
          <span className="inline-flex items-center rounded-full bg-white border border-[#ece6fb] px-3 py-1 text-[13px] font-semibold text-violet-700">
            Lista de espera
          </span>
          <h1 className="text-[2rem] leading-[1.12] sm:text-5xl sm:leading-[1.08] font-bold text-[#1e1b2e] mt-4">
            Sua futura <span className="text-grad">rede de apoio</span> começa aqui.
          </h1>
          <p className="text-muted-foreground mt-4 max-w-lg text-[15px] sm:text-base">
            Moradia compartilhada segura, com pessoas confiáveis. Entre para a lista de espera do MoraComigo e
            avisaremos você em primeira mão quando for a sua vez.
          </p>
          <ul className="hidden lg:grid gap-5 mt-10 max-w-md">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex gap-4">
                <span className="w-11 h-11 shrink-0 rounded-2xl mc-grad text-white flex items-center justify-center">
                  <Icon size={20} aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-semibold text-[#1e1b2e]">{title}</span>
                  <span className="block text-sm text-muted-foreground">{desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mc-card p-5 sm:p-8" aria-live="polite">
          {status === "done" ? (
            <div className="text-center py-6" data-testid="waitlist-success">
              <span className="mx-auto w-16 h-16 rounded-full mc-grad text-white flex items-center justify-center">
                <CheckCircle2 size={32} aria-hidden="true" />
              </span>
              <h2 ref={doneRef} tabIndex={-1} className="text-2xl font-bold text-[#1e1b2e] mt-5 outline-none">
                {firstName ? `Pronto, ${firstName}!` : "Pronto!"} Você está na lista.
              </h2>
              <p className="text-muted-foreground mt-3 max-w-sm mx-auto">
                Que bom ter você com a gente. Vamos avisar você por e-mail ou WhatsApp assim que
                for a sua vez — sem spam.
              </p>
              <Link to="/" className="btn-outline mt-7 inline-flex">Conhecer o MoraComigo</Link>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="waitlist-form" className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-[#1e1b2e]">Entre para a lista</h2>
                <p className="text-sm text-muted-foreground mt-1">Leva menos de um minuto.</p>
              </div>
              <Field id="le-name" label="Como podemos te chamar?" error={errors.name}>
                <input id="le-name" className="mc-input" autoComplete="given-name" maxLength={80}
                  value={form.name} onChange={set("name")} placeholder="Seu nome" data-testid="waitlist-name" />
              </Field>
              <Field id="le-email" label="E-mail" error={errors.email}>
                <input id="le-email" type="email" inputMode="email" className="mc-input" autoComplete="email" maxLength={254}
                  value={form.email} onChange={set("email")} placeholder="voce@email.com" data-testid="waitlist-email" />
              </Field>
              <Field id="le-whatsapp" label="WhatsApp" error={errors.whatsapp}>
                <input id="le-whatsapp" type="tel" inputMode="tel" className="mc-input" autoComplete="tel-national"
                  value={form.whatsapp} onChange={set("whatsapp")} placeholder="(43) 99999-0000" data-testid="waitlist-whatsapp" />
              </Field>
              <div className="grid grid-cols-[1fr_6.5rem] gap-3">
                <Field id="le-city" label="Cidade" error={errors.city}>
                  <input id="le-city" className="mc-input" autoComplete="address-level2" maxLength={80}
                    value={form.city} onChange={set("city")} placeholder="Sua cidade" data-testid="waitlist-city" />
                </Field>
                <Field id="le-state" label="Estado" error={errors.state}>
                  <select id="le-state" className="mc-input !px-3" autoComplete="address-level1"
                    value={form.state} onChange={set("state")} data-testid="waitlist-state">
                    <option value="">UF</option>
                    {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </Field>
              </div>
              {/* honeypot anti-bot: invisível para pessoas e leitores de tela */}
              <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
                <label>Site<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
              </div>
              <div>
                <label className="flex items-start gap-3 text-sm text-[#1e1b2e] cursor-pointer">
                  <input id="le-privacy" type="checkbox" checked={accepted} data-testid="waitlist-privacy"
                    onChange={(e) => { setAccepted(e.target.checked); setErrors((x) => ({ ...x, privacy: undefined })); }}
                    className="accent-violet-600 w-4 h-4 mt-0.5 shrink-0" aria-describedby="le-privacy-help" />
                  <span>
                    Concordo com a <span className="text-violet-600 font-semibold">Política de Privacidade</span>
                    <span id="le-privacy-help" className="block text-muted-foreground text-xs mt-1">
                      Usamos seus dados só para falar com você sobre o MoraComigo. Nada é exibido publicamente.
                    </span>
                  </span>
                </label>
                {errors.privacy && <p className="text-destructive text-xs mt-1" role="alert">{errors.privacy}</p>}
              </div>
              {formError && (
                <p className="text-sm text-destructive bg-red-50 rounded-xl px-4 py-3" role="alert" data-testid="waitlist-error">{formError}</p>
              )}
              <button type="submit" className="btn-primary w-full justify-center" disabled={status === "sending"}
                aria-busy={status === "sending"} data-testid="waitlist-submit">
                {status === "sending" ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Enviando…</> : "Entrar na lista de espera"}
              </button>
            </form>
          )}
        </div>

        <ul className="grid gap-4 lg:hidden">
          {BENEFITS.map(({ icon: Icon, title, desc }) => (
            <li key={title} className="flex gap-3 items-start">
              <span className="w-10 h-10 shrink-0 rounded-xl mc-grad text-white flex items-center justify-center">
                <Icon size={18} aria-hidden="true" />
              </span>
              <span>
                <span className="block font-semibold text-[#1e1b2e] text-[15px]">{title}</span>
                <span className="block text-sm text-muted-foreground">{desc}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Field({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-[#1e1b2e] mb-1.5">{label}</label>
      {cloneElement(children, { "aria-invalid": error ? true : undefined, "aria-describedby": error ? `${id}-error` : undefined })}
      {error && <p id={`${id}-error`} className="text-destructive text-xs mt-1">{error}</p>}
    </div>
  );
}
